import { useState, type FormEvent } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MarcaLogo from '@/components/feature/MarcaLogo';
import { NOMBRE_FARMACIA, SISTEMA_DESCRIPCION } from '@/lib/marca';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState('');
  const [contraseña, setContraseña] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!usuario.trim() || !contraseña.trim()) {
      setError('Complete todos los campos');
      return;
    }

    setLoading(true);
    const result = await login(usuario.trim(), contraseña);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Error al iniciar sesión');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50">
      <div className="w-full max-w-md px-4">
        <div className="bg-white rounded-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <MarcaLogo
                boxClassName="w-16 h-16"
                iconClassName="ri-medicine-bottle-line text-3xl text-white"
              />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-800">
              {NOMBRE_FARMACIA}
            </h1>
            <p className="text-foreground-500 text-sm mt-1">
              {SISTEMA_DESCRIPCION}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {error && (
              <div className="bg-accent-50 border border-accent-200 text-accent-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="usuario" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Usuario
              </label>
              <div className="relative">
                <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  id="usuario"
                  type="text"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="Ingrese su usuario"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contraseña" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  id="contraseña"
                  type={showPassword ? 'text' : 'password'}
                  value={contraseña}
                  onChange={(e) => setContraseña(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="Ingrese su contraseña"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer p-1"
                  tabIndex={-1}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <i className="ri-login-box-line"></i>
                  <span>Iniciar Sesión</span>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-foreground-400 text-xs mt-6">
          &copy; {new Date().getFullYear()} {NOMBRE_FARMACIA}. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}